/**
 * Stage-to-phase mapping and display labels for pipeline tabs.
 * Single source of truth for how stages are grouped and labeled.
 */

export type PipelinePhase = "leads" | "claims" | "reinspections" | "builds" | "closing";

export const PIPELINE_PHASES: PipelinePhase[] = [
  "leads",
  "claims",
  "reinspections",
  "builds",
  "closing",
];

export const PHASE_LABELS: Record<PipelinePhase, string> = {
  leads: "Leads",
  claims: "Claims",
  reinspections: "Reinspections",
  builds: "Builds",
  closing: "Closing",
};

export const PHASE_COLORS: Record<PipelinePhase, string> = {
  leads: "bg-blue-500",
  claims: "bg-amber-500",
  reinspections: "bg-purple-500",
  builds: "bg-emerald-500",
  closing: "bg-bardin-teal",
};

/** Stages within each phase, in pipeline order */
export const PHASE_STAGES: Record<PipelinePhase, string[]> = {
  leads: [
    "initial_contact",
    "presentation_scheduled",
    "follow_up",
    "waiting",
    "no_thanks",
  ],
  claims: [
    "assign_roof_specialist",
    "pre_inspection_ready",
    "adjuster_meeting_ready",
    "waiting_on_claims",
    "claims_denied",
  ],
  reinspections: [
    "partial_approval",
    "docs_and_contingency",
    "reinspection_ready",
    "reinspection_filed",
  ],
  builds: [
    "install_meeting",
    "build_submitted",
    "schedule_build",
    "build_scheduled",
  ],
  closing: [
    // roof_complete retired May 7, 2026 (kept in STAGE_LABELS for legacy display)
    "trades_in_progress",
    "trades_complete",
    "collecting",
    "collect_payment",
    "supps_waiting_on_claims",
    "commission_ready",
    "commission_approved",
    "commission_paid",
  ],
};

/** Human-readable stage labels */
export const STAGE_LABELS: Record<string, string> = {
  // Leads
  initial_contact: "Initial Contact",
  presentation_scheduled: "Presentation Scheduled",
  follow_up: "Follow Up",
  waiting: "Waiting",
  no_thanks: "No Thanks",
  // Claims
  assign_roof_specialist: "Assign RS",
  pre_inspection_ready: "PRE Ready",
  pre_inspection_complete: "PRE Complete",
  adjuster_meeting_ready: "Adjuster Ready",
  adjuster_meeting_scheduled: "Adjuster Scheduled",
  waiting_on_claims: "Waiting on Claims",
  claims_denied: "Denied",
  // Reinspections
  partial_approval: "Partial Approval",
  docs_and_contingency: "Docs & Contingency",
  reinspection_ready: "Reinspection Ready",
  reinspection_filed: "Reinspection Filed",
  // Builds
  install_meeting: "Install Meeting",
  build_submitted: "Build Submitted",
  schedule_build: "Schedule Build",
  build_scheduled: "Build Scheduled",
  // Closing
  roof_complete: "Roof Complete",
  trades_in_progress: "Trades In Progress",
  trades_complete: "Trades Complete",
  collect_payment: "Collect Payment",
  commission_ready: "Commission Ready",
  commission_approved: "Commission Approved",
  collecting: "Collecting",
  supps_waiting_on_claims: "Supps Waiting on Claims",
  commission_paid: "Commission Paid",
  // Closed
  closed_won: "Closed Won",
  closed_lost: "Closed Lost",
  cancelled: "Cancelled",
  closed_inspection: "Closed Inspection",
  // Drone (not in pipeline tabs but may appear in data)
  pending_asm_review: "Pending ASM Review",
  appointment_received: "Appointment Received",
  drone_scheduled: "Drone Scheduled",
  drone_complete: "Drone Complete",
  report_in_progress: "Report In Progress",
  qc_review: "QC Review",
  report_approved: "Report Approved",
  needs_climb_inspection: "Needs Climb",
  insufficient_damage: "Insufficient Damage",
};
